import React from 'react';
import { motion } from 'framer-motion';
import {
  CameraIcon,
  SparklesIcon,
  CheckBadgeIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import { User } from '../../types';
import { getUserRoleLabel } from '../../utils/format';
import AvailabilityToggle from '../profile/AvailabilityToggle';
import Avatar from '../common/Avatar';

interface WelcomeBannerProps {
  user: User;
  today_stats: {
    upcoming_events: number;
    pending_messages: number;
    new_opportunities: number;
    new_applications?: number;
  };
}

const WelcomeBanner: React.FC<WelcomeBannerProps> = ({ user, today_stats }) => {
  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Determine what to show in the third card
  const getThirdCardData = () => {
    // Hirer specific data
    if (user.user_type === 'hirer') {
      return {
        count: today_stats.new_opportunities,
        label: 'My Posted Opportunities'
      };
    }

    // If there are new applications (for anyone who created an opportunity), show that
    if (today_stats.new_applications && today_stats.new_applications > 0) {
      return {
        count: today_stats.new_applications,
        label: 'New Applications'
      };
    }
    
    // Fallback for Creator (should match new_applications anyway, but safe fallback)
    if (user.user_type === 'creator') {
       return {
        count: today_stats.new_opportunities, // This is mirrored from new_applications in backend
        label: 'New Applications'
      };
    }

    // Fallback for Model (System opportunities)
    return {
      count: today_stats.new_opportunities,
      label: 'New Opportunities'
    };
  };

  const thirdCard = getThirdCardData();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-black rounded-2xl md:rounded-3xl p-4 md:p-8 text-white mb-4 md:mb-6 relative overflow-hidden shadow-2xl"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-gray-800 blur-3xl transform translate-x-32 -translate-y-32"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-gray-900 blur-3xl transform -translate-x-24 translate-y-24">
        </div>
      </div>

      <div className="relative z-10">
        <div className="flex items-center justify-between mb-4 md:mb-8">
          <div className="flex items-center space-x-3 md:space-x-6 min-w-0">
            {/* Profile Picture */}
            <div className="relative flex-shrink-0">
              <Avatar
                src={user.profile_pic_thumb_signed || user.profile_pic}
                alt={`${user.first_name} ${user.last_name}`}
                size="lg"
                className="border-2 md:border-4 border-white/10 ring-1 ring-white/20 w-12 h-12 md:w-20 md:h-20"
              />
              
              {/* Status Indicator */}
              {user.user_type !== 'hirer' && (
                <div className={`absolute -bottom-1 -right-1 w-4 h-4 md:w-6 md:h-6 rounded-full border-2 md:border-4 border-black ${
                  user.is_available ? 'bg-green-500' : 'bg-gray-500'
                }`}></div>
              )}
            </div>

            {/* Greeting & User Info */}
            <div className="min-w-0">
              <h1 className="text-xl md:text-3xl font-bold mb-0.5 md:mb-2 tracking-tight truncate">
                <span className="md:hidden">Hi, {user.first_name}!</span>
                <span className="hidden md:inline">{getTimeBasedGreeting()}, {user.first_name}! 👋</span>
              </h1>
              {/* Role badge (Desktop Only) */}
              <div className="hidden md:flex items-center space-x-2 text-gray-400">
                <div className="flex items-center space-x-1.5 bg-white/10 px-3 py-1 rounded-full text-sm backdrop-blur-sm">
                  {user.user_type === 'creator' ? (
                    <CameraIcon className="h-4 w-4" />
                  ) : (
                    <SparklesIcon className="h-4 w-4" />
                  )}
                  <span className="font-medium">{getUserRoleLabel(user)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Status Toggle (Desktop Only) */}
          {user.user_type !== 'hirer' && (
            <div className="hidden md:flex items-center bg-white/5 rounded-full px-4 py-2 backdrop-blur-sm border border-white/10">
              <AvailabilityToggle 
                  isAvailable={user.is_available ?? false} 
                  userId={user.id} 
                  textClasses='text-sm text-gray-300 font-medium'
                  showStatus={true} 
                  size="md"
              />
            </div>
          )}

          {/* Status Toggle (Mobile Only) */}
          {user.user_type !== 'hirer' && (
            <div className="md:hidden flex items-center bg-white/5 rounded-full px-2.5 py-1.5 border border-white/10 backdrop-blur-sm flex-shrink-0 ml-2">
              <AvailabilityToggle 
                  isAvailable={user.is_available ?? false} 
                  userId={user.id} 
                  textClasses='hidden'
                  showStatus={true} 
                  size="sm"
                  textSide="left"
              />
            </div>
          )}
        </div>

        {/* Mobile-Only Role Badge Row */}
        <div className="md:hidden mb-4">
          <div className="inline-flex items-center space-x-1.5 bg-white/10 px-3 py-1 rounded-full text-[11px] backdrop-blur-sm max-w-full">
            {user.user_type === 'creator' ? (
              <CameraIcon className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" />
            ) : (
              <SparklesIcon className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" />
            )}
            <span className="font-medium text-gray-200 truncate">{getUserRoleLabel(user)}</span>
          </div>
        </div>

        {/* Mobile: Compact Stats Row */}
        <div className="flex md:hidden justify-between items-center bg-white/5 rounded-xl p-3 backdrop-blur-sm">
            <div className="text-center flex-1 border-r border-white/10">
                <div className="text-lg font-bold text-white leading-none mb-1">{today_stats.upcoming_events}</div>
                <div className="text-[9px] text-gray-400 font-medium uppercase tracking-tight">{user.user_type === 'creator' || user.user_type === 'hirer' ? 'Active' : 'Shoots'}</div>
            </div>
            <div className="text-center flex-1 border-r border-white/10">
                <div className="text-lg font-bold text-white leading-none mb-1">{today_stats.pending_messages}</div>
                <div className="text-[9px] text-gray-400 font-medium uppercase tracking-tight">Msgs</div>
            </div>
            <div className="text-center flex-1">
                <div className="text-lg font-bold text-white leading-none mb-1">{thirdCard.count}</div>
                <div className="text-[9px] text-gray-400 font-medium uppercase tracking-tight truncate max-w-[80px]" title={thirdCard.label}>
                    {thirdCard.label.replace('New ', '')}
                </div>
            </div>
        </div>

        {/* Desktop: Stats Grid (Hidden on Mobile) */}
        <div className="hidden md:grid grid-cols-3 gap-4">
          <div className="bg-white/10 rounded-2xl p-5 backdrop-blur-md border border-white/5 hover:bg-white/15 transition-colors">
            <div className="flex items-center space-x-3 mb-2">
              <div className="p-2 bg-white/10 rounded-lg">
                <CalendarDaysIcon className="h-5 w-5 text-white" />
              </div>
              <span className="text-sm text-gray-300 font-medium">Today</span>
            </div>
            <div className="text-3xl font-bold mb-1">{today_stats.upcoming_events}</div>
            <div className="text-xs text-gray-400 font-medium uppercase tracking-wide">
              {user.user_type === 'creator' || user.user_type === 'hirer' ? 'Active Opportunities' : 'Upcoming Shoots'}
            </div>
          </div>

          <div className="bg-white/10 rounded-2xl p-5 backdrop-blur-md border border-white/5 hover:bg-white/15 transition-colors">
            <div className="flex items-center space-x-3 mb-2">
              <div className="p-2 bg-white/10 rounded-lg">
                <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center">
                  <span className="text-black text-xs font-bold">M</span>
                </div>
              </div>
              <span className="text-sm text-gray-300 font-medium">Messages</span>
            </div>
            <div className="text-3xl font-bold mb-1">{today_stats.pending_messages}</div>
            <div className="text-xs text-gray-400 font-medium uppercase tracking-wide">Pending replies</div>
          </div>

          <div className="bg-white/10 rounded-2xl p-5 backdrop-blur-md border border-white/5 hover:bg-white/15 transition-colors">
            <div className="flex items-center space-x-3 mb-2">
              <div className="p-2 bg-white/10 rounded-lg">
                {/* Dynamic Icon based on content */}
                {thirdCard.label.includes('Applications') ? (
                  <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                    <span className="text-[10px] font-bold text-white">A</span>
                  </div>
                ) : thirdCard.label.includes('Posted') ? (
                  <div className="w-5 h-5 rounded-full bg-teal-500 flex items-center justify-center">
                    <span className="text-[10px] font-bold text-white">P</span>
                  </div>
                ) : (
                  <div className="w-5 h-5 rounded-full bg-green-500"></div>
                )}
              </div>
              <span className="text-sm text-gray-300 font-medium">{user.user_type === 'hirer' ? 'Posted' : 'New'}</span>
            </div>
            <div className="text-3xl font-bold mb-1">{thirdCard.count}</div>
            <div className="text-xs text-gray-400 font-medium uppercase tracking-wide truncate" title={thirdCard.label}>
              {thirdCard.label.replace('New ', '')}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default WelcomeBanner;
